import * as React from 'react'
import { MoreHorizontal, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { useViewOnly, useViewOnlyBadge } from './app-shell'

export type ListPageTableState = 'ready' | 'loading' | 'error'

export interface ListPageColumn<TRow> {
  id: string
  header: React.ReactNode
  cell: (row: TRow) => React.ReactNode
  className?: string
  headerClassName?: string
  sort?: {
    active: boolean
    direction: 'ascending' | 'descending'
    onToggle: () => void
  }
}

export interface ListPageRowAction<TRow> {
  label: string
  onSelect: (row: TRow) => void
  disabled?: boolean | ((row: TRow) => boolean)
  destructive?: boolean
}

export interface ListPageTable<TRow> {
  /** Shared cell spacing for compact, multi-line records. */
  cellClassName?: string
  rows: TRow[]
  columns: ListPageColumn<TRow>[]
  getRowId: (row: TRow) => React.Key
  getRowLabel: (row: TRow) => string
  onRowOpen?: (row: TRow) => void
  actions?: (row: TRow) => ListPageRowAction<TRow>[]
  state?: ListPageTableState
  loadingTitle?: string
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: React.ReactNode
  errorTitle?: string
  errorDescription?: string
  emptyVariant?: 'default' | 'quiet'
  /** Rendered inside the card above the column headings when there are rows (a pager, a notice). */
  header?: React.ReactNode
  footer?: React.ReactNode
}

export interface ListPageProps<TRow = unknown> {
  title: string
  description?: string
  search?: { value: string; onChange: (value: string) => void; placeholder?: string }
  primaryAction?: { label: string; onClick?: () => void; disabled?: boolean }
  /** Extra controls beside search (filters, an "include archived" toggle, etc.). */
  toolbar?: React.ReactNode
  /** A shadcn data-table block owned by the template. */
  table?: ListPageTable<TRow>
  /** Escape hatch for non-table content inside the standard list frame. */
  children?: React.ReactNode
}

/**
 * The archetype for any collection screen (contacts, events, …): a title with a
 * primary create action, a search/filter row, and a bordered surface for the rows.
 */
export function ListPage<TRow = unknown>({
  title,
  description,
  search,
  primaryAction,
  toolbar,
  table,
  children,
}: ListPageProps<TRow>) {
  const viewOnly = useViewOnly()
  const showViewOnlyBadge = useViewOnlyBadge()
  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-heading-sm font-semibold text-foreground">{title}</h1>
          {description && <p className="pt-1 text-body-sm text-muted-foreground">{description}</p>}
        </div>
        {showViewOnlyBadge && <Badge variant="info">View only</Badge>}
        {primaryAction && !viewOnly && (
          <Button type="button" onClick={primaryAction.onClick} disabled={primaryAction.disabled}>
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} />
            {primaryAction.label}
          </Button>
        )}
      </header>

      {(search || toolbar) && (
        <div className="flex flex-wrap items-center gap-3 pb-4">
          {search && (
            <div className="relative w-72">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                strokeWidth={1.5}
              />
              <Input
                className="pl-9"
                value={search.value}
                placeholder={search.placeholder ?? 'Search…'}
                onChange={(e) => search.onChange(e.target.value)}
              />
            </div>
          )}
          {toolbar}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-border bg-card">
        {table ? <DataTableBlock table={table} /> : children}
      </div>
    </div>
  )
}

export function DataTableBlock<TRow>({ table }: { table: ListPageTable<TRow> }) {
  const state = table.state ?? 'ready'
  const viewOnly = useViewOnly()

  if (state === 'loading') {
    return <EmptyState title={table.loadingTitle ?? 'Loading records'} className="min-h-52" />
  }

  if (state === 'error') {
    return (
      <EmptyState
        title={table.errorTitle ?? 'Records unavailable'}
        description={table.errorDescription}
        className="min-h-52"
      />
    )
  }

  if (table.rows.length === 0) {
    return (
      <EmptyState
        title={table.emptyTitle ?? 'No records yet'}
        description={table.emptyDescription}
        action={table.emptyAction}
        className="min-h-52"
        variant={table.emptyVariant}
      />
    )
  }

  const hasActions = Boolean(table.actions) && !viewOnly

  return (
    <>
      {table.header}
      <Table>
        <TableHeader>
          <TableRow>
            {table.columns.map((column) =>
              column.sort ? (
                <SortableTableHead
                  key={column.id}
                  className={column.headerClassName}
                  {...column.sort}
                >
                  {column.header}
                </SortableTableHead>
              ) : (
                <TableHead key={column.id} className={column.headerClassName}>
                  {column.header}
                </TableHead>
              ),
            )}
            {hasActions && <TableHead className="w-12" aria-label="Row actions" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.rows.map((row) => {
            const rowLabel = table.getRowLabel(row)
            const rowActions = viewOnly ? [] : (table.actions?.(row) ?? [])
            const canOpen = Boolean(table.onRowOpen)

            return (
              <TableRow
                key={table.getRowId(row)}
                tabIndex={canOpen ? 0 : undefined}
                onClick={canOpen ? () => table.onRowOpen?.(row) : undefined}
                onKeyDown={(event) => {
                  if (!canOpen || (event.key !== 'Enter' && event.key !== ' ')) return

                  event.preventDefault()
                  table.onRowOpen?.(row)
                }}
                className={cn(
                  'group',
                  canOpen &&
                    'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                )}
              >
                {table.columns.map((column) => (
                  <TableCell key={column.id} className={cn(table.cellClassName, column.className)}>
                    {column.cell(row)}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell className={cn('w-12 py-1 text-right', table.cellClassName)}>
                    {rowActions.length > 0 && (
                      <RowActions label={`${rowLabel} actions`} row={row} actions={rowActions} />
                    )}
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {table.footer}
    </>
  )
}

function RowActions<TRow>({
  label,
  row,
  actions,
}: {
  label: string
  row: TRow
  actions: ListPageRowAction<TRow>[]
}) {
  return (
    <div className="inline-flex">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 w-9 px-0 opacity-100"
            aria-label={label}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map((action) => (
            <DropdownMenuItem
              key={action.label}
              disabled={actionDisabled(action, row)}
              destructive={action.destructive}
              onClick={(event) => event.stopPropagation()}
              onSelect={(event) => {
                event.stopPropagation()
                action.onSelect(row)
              }}
            >
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function actionDisabled<TRow>(action: ListPageRowAction<TRow>, row: TRow) {
  return typeof action.disabled === 'function' ? action.disabled(row) : action.disabled
}
